// Proactive token refresh service with configurable session settings
import StorageService from './storage';
import { SessionSettings } from '../types/auth';
import { LogoutCoordinator } from './logoutCoordinator';
import { coordinatedRefresh } from './refreshCoordinator';

// Don't fire a foreground/online refresh more often than this. Without it,
// every focus/visibilitychange/online event at app startup (and they fire in a
// burst on load) would each rotate the refresh token, racing the page's own
// data loads — exactly the churn that produced spurious 401s on mobile. A
// genuine return from background happens minutes later, so this cooldown never
// suppresses the case the handler exists for.
const FOREGROUND_REFRESH_COOLDOWN_MS = 30 * 1000;

class TokenRefreshService {
  private refreshTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private isStarted = false;
  private refreshIntervalMs = 13 * 60 * 1000;
  private foregroundHandlersInstalled = false;
  private lastForegroundRefreshAt = 0;
  private sessionSettingsListeners: Array<(settings: SessionSettings) => void> = [];

  constructor() {
    // Seed the cooldown so the burst of focus/visibility events fired during
    // initial page load doesn't trigger an immediate (startup-racing) refresh —
    // the app already validates/refreshes on load.
    this.lastForegroundRefreshAt = Date.now();
    this.installForegroundHandlers();
  }

  registerSessionSettingsListener(listener: (settings: SessionSettings) => void): void {
    this.sessionSettingsListeners.push(listener);
  }

  /**
   * Mobile browsers (Chrome/Safari on iOS & Android) throttle or completely
   * freeze setInterval/setTimeout while a tab is backgrounded — when the phone
   * locks or the user switches apps. The proactive refresh timer below therefore
   * never fires while away, so the access cookie can expire silently. When the
   * user returns, the first request 401s and (previously) cascaded to a logout.
   *
   * The reliable signal on mobile is the `visibilitychange` event (and `online`
   * after a network drop). On returning to the foreground we proactively run a
   * single coordinated refresh BEFORE the app's stale requests fire, so the
   * session is already fresh and nothing 401s. This is the primary mechanism
   * that keeps mobile sessions alive; the interval timer is just a backstop.
   */
  private installForegroundHandlers(): void {
    if (this.foregroundHandlersInstalled || typeof window === 'undefined') {
      return;
    }
    this.foregroundHandlersInstalled = true;

    const onForeground = () => {
      // Only refresh when the tab is actually visible and a user is present.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      void this.refreshOnForeground();
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onForeground);
    }
    window.addEventListener('focus', onForeground);
    window.addEventListener('online', onForeground);

    // Mobile back-forward cache (bfcache) restores fire `pageshow` with
    // persisted=true — NOT visibilitychange. iOS Safari in particular can
    // restore a page from bfcache without the document ever transitioning
    // through the Page Visibility API's hidden state, so the listeners above
    // alone miss this case entirely and the access cookie is never refreshed
    // before reconnecting WebSockets/data fetches race in on the stale one.
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        onForeground();
      }
    });
  }

  /**
   * Triggered when the tab returns to the foreground / network comes back.
   * Refreshes the token proactively so queued mobile requests don't 401.
   */
  private async refreshOnForeground(): Promise<void> {
    // Nothing to do if there's no logged-in user or a logout is already underway.
    if (!StorageService.getUser() || LogoutCoordinator.active) {
      return;
    }
    if (this.isRefreshing) {
      return;
    }

    // Debounce: a single return-to-foreground fires several events
    // (visibilitychange + focus, sometimes online). Only refresh once per
    // cooldown so we don't rotate the token repeatedly and race in-flight loads.
    const now = Date.now();
    if (now - this.lastForegroundRefreshAt < FOREGROUND_REFRESH_COOLDOWN_MS) {
      return;
    }
    this.lastForegroundRefreshAt = now;

    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[TokenRefreshService] Foreground/online event — proactively refreshing token');
    }

    const refreshed = await this.refreshTokenIfNeeded();
    // Re-arm the interval timer from a clean baseline so the next proactive
    // refresh is scheduled relative to *now*, not the frozen pre-background time.
    if (refreshed && this.isStarted) {
      this.scheduleRefreshTimer();
    }
  }

  private notifySessionSettings(settings: SessionSettings): void {
    for (const listener of this.sessionSettingsListeners) {
      try {
        listener(settings);
      } catch (err) {
        console.error('Session settings listener error:', err);
      }
    }
  }

  private computeRefreshIntervalMs(): number {
    const debug = process.env.REACT_APP_DEBUG_VERBOSE_API === 'true';
    if (debug) console.log('[TokenRefreshService] === computeRefreshIntervalMs() ===');

    const settings = StorageService.getSessionSettings();
    const configuredInterval = Math.max(1, settings?.session_check_interval_minutes ?? 5);

    // If neither timeout feature is enabled, just use the configured check interval.
    const anyTimeoutEnabled = settings?.max_login_duration_enabled || settings?.idle_timeout_enabled;
    if (!anyTimeoutEnabled) {
      const intervalMs = configuredInterval * 60 * 1000;
      if (debug) console.log('[TokenRefreshService] Both timeouts disabled, interval:', configuredInterval, 'min');
      return intervalMs;
    }

    // Determine the shortest active timeout.
    let shortestMinutes = Infinity;
    if (settings?.idle_timeout_enabled) {
      shortestMinutes = Math.min(shortestMinutes, settings.idle_timeout_minutes ?? 20);
    }
    if (settings?.max_login_duration_enabled) {
      shortestMinutes = Math.min(shortestMinutes, settings.max_login_duration_minutes ?? 480);
    }
    shortestMinutes = Math.max(1, shortestMinutes);

    // Ensure we refresh before the shortest timer fires (leave a buffer).
    const maxIntervalMinutes = Math.max(1, shortestMinutes - 10);
    const intervalMinutes = Math.min(configuredInterval, maxIntervalMinutes);
    const intervalMs = intervalMinutes * 60 * 1000;

    if (debug) console.log('[TokenRefreshService] Computed interval:', intervalMinutes, 'min');
    return intervalMs;
  }

  private scheduleRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }

    this.refreshIntervalMs = this.computeRefreshIntervalMs();
    this.refreshTimer = setInterval(() => {
      void this.refreshTokenIfNeeded();
    }, this.refreshIntervalMs);

    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log(`?? Auto token refresh scheduled every ${this.refreshIntervalMs / 60000} minutes`);
    }
  }

  startAutoRefresh(forceRestart: boolean = false): void {
    if (this.isStarted && this.refreshTimer && !forceRestart) {
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('?? Auto token refresh already running, skipping duplicate start');
      }
      return;
    }

    this.scheduleRefreshTimer();
    this.isStarted = true;
  }

  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.isStarted = false;
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('?? Auto token refresh stopped');
    }
  }

  private async refreshTokenIfNeeded(): Promise<boolean> {
    if (this.isRefreshing) {
      return false;
    }

    const user = StorageService.getUser();
    if (!user) {
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('? No user found, stopping auto refresh');
      }
      this.stopAutoRefresh();
      return false;
    }

    this.isRefreshing = true;

    try {
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('?? Proactively refreshing token...');
      }

      // Use the shared coordinator so this proactive refresh can never race a
      // 401-driven refresh from the axios interceptors against the rotating
      // /auth/refresh/ endpoint. See refreshCoordinator.ts.
      const result = await coordinatedRefresh();

      if (result.ok) {
        const data = result.data || {};
        if (data?.session_settings) {
          // Guard: don't overwrite hospital-specific settings with unresolved defaults
          const currentSettings = StorageService.getSessionSettings();
          const incoming = data.session_settings;
          if (currentSettings?.hospital_id && !incoming.hospital_id) {
            if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
              console.log(
                `[TokenRefreshService] Ignoring default session settings (hospital_id=null); keeping hospital-specific settings (hospital_id=${currentSettings.hospital_id})`
              );
            }
          } else {
            StorageService.setSessionSettings(incoming);
            this.notifySessionSettings(incoming);
            if (this.isStarted) {
              this.scheduleRefreshTimer();
            }
          }
        }

        // Update last activity timestamp after successful refresh
        StorageService.updateLastActivity();

        if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
          console.log('? Token refreshed proactively');
        }
        return true;
      }

      // A genuine auth failure (refresh token expired/revoked) means the user
      // must re-authenticate, so stop the proactive loop. Transient network /
      // gateway errors are reported as status 0 / 5xx — do NOT stop the loop on
      // those, or a brief offline blip on mobile would permanently disable
      // proactive refresh. (Note: the coordinator retries 5xx internally but
      // NOT network errors, since this endpoint rotates the refresh token and a
      // retry could re-present one the server already rotated. So a status 0
      // here is a single failed attempt, which makes this guard load-bearing.)
      const isAuthFailure = result.status === 401 || result.status === 403;
      if (isAuthFailure) {
        console.warn('?? Token refresh rejected (auth failure), stopping auto refresh');
        this.stopAutoRefresh();
      } else if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log(
          `[TokenRefreshService] Proactive refresh failed transiently (status=${result.status}); keeping timer running`
        );
      }
      return false;
    } catch (error) {
      console.error('? Auto token refresh error:', error);
      // Keep the loop alive on unexpected errors; the next tick (or a
      // foreground event) will retry rather than locking the user out.
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  async manualRefresh(): Promise<boolean> {
    if (this.isRefreshing) {
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('?? Refresh already in progress');
      }
      return false;
    }

    return this.refreshTokenIfNeeded();
  }
}

const tokenRefreshService = new TokenRefreshService();
export default tokenRefreshService;
